import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
    collection, query, where, getDocs, updateDoc, doc, Timestamp, documentId,
} from 'firebase/firestore';
import {
    Menu, LogOut, Phone, MessageCircle, PhoneIncoming, PhoneMissed, Search, RefreshCw,
    Check, CalendarClock, Clock, X, Filter,
} from 'lucide-react';
import DashboardNav from './DashboardNav';
import { NextCallPicker } from './CallFollowUp';
import { toLocalPhone } from './CallHistory';
import { daysFromToday, fmtYMD } from './CustomerProfileModal';
import { auth, db } from './firebase';

/**
 * ─────────────────────────────────────────────────────────────
 *  Call Report
 *   ট্যাব ১: Call Report — কোন দিন/সময়ে কতগুলো কল হয়েছে, কোন অ্যাডমিন (email) কাকে কল করেছে,
 *            কল যাওয়া কাস্টমারের অর্ডার Delivered / Return / Cancel কোনটা।
 *            ডাটা: callRecords (কল সার্ভারের call-history.js সেভ করে):
 *              • username = কল করা অ্যাডমিনের Firebase email
 *              • date     = কলের তারিখ 'YYYY-MM-DD' (বাংলাদেশ সময়, UTC+6)
 *              • linkedTo = ['orders/{id}', 'Deliveryman_list/{id}', 'withoutorders/{id}', ...]
 *   ট্যাব ২: Next Call Due — orders/{id}.nextCall.status == 'pending' এর লিস্ট (কতদিন পর কল দিতে হবে)।
 *  শুধু পড়া হয়; Due ট্যাবে Done / Reschedule করলে শুধু nextCall ফিল্ড বদলায়।
 * ─────────────────────────────────────────────────────────────
 */

const TYPES = [
    { key: 'Delivered', label: 'Delivered', box: 'bg-green-50 border-green-200', num: 'text-green-700', badge: 'bg-green-100 text-green-700' },
    { key: 'Return', label: 'Return', box: 'bg-purple-50 border-purple-200', num: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
    { key: 'Cancel', label: 'Cancel', box: 'bg-red-50 border-red-200', num: 'text-red-700', badge: 'bg-red-100 text-red-700' },
    { key: 'Other', label: 'অন্যান্য / অজানা', box: 'bg-gray-50 border-gray-200', num: 'text-gray-700', badge: 'bg-gray-100 text-gray-600' },
];
const TYPE_BY_KEY = Object.fromEntries(TYPES.map((t) => [t.key, t]));
const bucketOf = (status) => (status === 'Delivered' || status === 'Return' || status === 'Cancel' ? status : 'Other');

const STATUS_STYLE = {
    'Call Ended': 'bg-green-100 text-green-700',
    'No Answer': 'bg-amber-100 text-amber-700',
    'Busy': 'bg-amber-100 text-amber-700',
    'Rejected': 'bg-red-100 text-red-700',
    'Unavailable': 'bg-red-100 text-red-700',
    'Call Failed': 'bg-red-100 text-red-700',
    'Cancelled': 'bg-gray-100 text-gray-600',
};

const pad = (n) => String(n).padStart(2, '0');
const chunk = (arr, n) => {
    const out = [];
    for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
};

const fmtDuration = (sec) => {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
};

/** CallHistory.js এর callMillis এর মতোই */
const callMillis = (c) => {
    if (c.timestamp) return Number(c.timestamp) * 1000;
    const ms = c.createdAt?.toMillis?.();
    if (ms) return ms;
    const t = Date.parse(String(c.start_time || '').replace(' ', 'T'));
    return Number.isNaN(t) ? 0 : t;
};
const orderMillis = (o) => o?.created_at?.toMillis?.() || (o?.created_at ? new Date(o.created_at).getTime() || 0 : 0);

/** start_time ('YYYY-MM-DD HH:mm:ss', বাংলাদেশ সময়) → '30/10/2026 14:05' */
const fmtCallTime = (c) => {
    const m = String(c.start_time || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (m) return `${Number(m[3])}/${Number(m[2])}/${m[1]} ${m[4]}:${m[5]}`;
    const ms = callMillis(c);
    if (!ms) return '—';
    const d = new Date(ms);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** বাংলাদেশের (UTC+6) তারিখ 'YYYY-MM-DD', offset দিন আগে/পরে — callRecords.date এর সাথে মিলানোর জন্য */
const dhakaYMD = (offsetDays = 0) => {
    const d = new Date(Date.now() + 6 * 3600 * 1000 + offsetDays * 86400000);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
const isYMD = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

/** কলটা কোন ধরনের কাস্টমারের (অর্ডার না পেলে ফ্ল্যাগ দেখে) */
const orphanLabel = (c) => {
    if (c.linkedToDeliveryman) return 'Deliveryman';
    if (c.linkedToWithoutOrder) return 'অর্ডার করেনি (Lead)';
    return 'অর্ডার পাওয়া যায়নি';
};

/* ───────────────────────── Call Report data ───────────────────────── */

async function loadReport(from, to) {
    // callRecords.date = কলের তারিখ (বাংলাদেশ সময়) — একটাই কুয়েরি, কোনো কম্পোজিট ইনডেক্স লাগে না
    const results = await Promise.allSettled([
        getDocs(query(collection(db, 'callRecords'), where('date', '>=', from), where('date', '<=', to))),
    ]);
    const map = new Map();
    let failed = 0;
    results.forEach((r) => {
        if (r.status === 'fulfilled') {
            r.value.forEach((d) => { if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() }); });
        } else {
            failed += 1;
            console.error('callRecords read failed:', r.reason);
        }
    });

    const calls = Array.from(map.values());

    // কলের সাথে linkedTo ["orders/{id}"] থেকে order বের করি
    const idsOf = (c) => (Array.isArray(c.linkedTo) ? c.linkedTo : [])
        .filter((x) => typeof x === 'string' && x.startsWith('orders/'))
        .map((x) => x.split('/')[1])
        .filter(Boolean);

    const orderIds = Array.from(new Set(calls.flatMap(idsOf)));
    const orderMap = new Map();
    await Promise.allSettled(chunk(orderIds, 30).map((ids) =>
        getDocs(query(collection(db, 'orders'), where(documentId(), 'in', ids)))
            .then((s) => s.forEach((d) => orderMap.set(d.id, { id: d.id, ...d.data() })))
    ));

    // linkedTo না থাকলে ফোন নাম্বার দিয়ে খুঁজি (customerInfo.phone == 01XXXXXXXXX)
    const unlinked = Array.from(new Set(
        calls
            .filter((c) => !idsOf(c).some((id) => orderMap.has(id)))
            .map((c) => toLocalPhone(c.number))
            .filter((p) => p.length >= 10)
    )).slice(0, 400);
    const phoneMap = new Map();
    await Promise.allSettled(chunk(unlinked, 30).map((ph) =>
        getDocs(query(collection(db, 'orders'), where('customerInfo.phone', 'in', ph)))
            .then((s) => s.forEach((d) => {
                const o = { id: d.id, ...d.data() };
                const key = toLocalPhone(o.customerInfo?.phone);
                const prev = phoneMap.get(key);
                if (!prev || orderMillis(o) > orderMillis(prev)) phoneMap.set(key, o);
            }))
    ));

    const enriched = calls.map((c) => {
        let o = null;
        idsOf(c).forEach((id) => {
            const x = orderMap.get(id);
            if (x && (!o || orderMillis(x) > orderMillis(o))) o = x;
        });
        if (!o) o = phoneMap.get(toLocalPhone(c.number)) || null;
        return { ...c, order: o, bucket: bucketOf(o?.status) };
    }).sort((a, b) => callMillis(b) - callMillis(a));

    return { calls: enriched, failed, total: results.length };
}

/* ───────────────────────── Tab 1: Call Report ───────────────────────── */

function ReportTab() {
    const [preset, setPreset] = useState('today');
    const [from, setFrom] = useState(() => dhakaYMD(0));
    const [to, setTo] = useState(() => dhakaYMD(0));
    const [state, setState] = useState({ loading: true, calls: [], failed: 0, total: 0 });
    const [reload, setReload] = useState(0);

    const [typeF, setTypeF] = useState('all');
    const [callerF, setCallerF] = useState('all');
    const [resultF, setResultF] = useState('all');
    const [search, setSearch] = useState('');
    const [visible, setVisible] = useState(50);

    useEffect(() => {
        if (!isYMD(from) || !isYMD(to) || from > to) return undefined;
        let alive = true;
        setState((s) => ({ ...s, loading: true }));
        loadReport(from, to)
            .then((r) => { if (alive) setState({ loading: false, ...r }); })
            .catch((e) => {
                console.error(e);
                if (alive) setState({ loading: false, calls: [], failed: 1, total: 1 });
            });
        return () => { alive = false; };
    }, [from, to, reload]);

    useEffect(() => { setVisible(50); }, [typeF, callerF, resultF, search, from, to]);

    const applyPreset = (p) => {
        setPreset(p);
        if (p === 'today') { setFrom(dhakaYMD(0)); setTo(dhakaYMD(0)); }
        if (p === 'yesterday') { setFrom(dhakaYMD(-1)); setTo(dhakaYMD(-1)); }
        if (p === '7d') { setFrom(dhakaYMD(-6)); setTo(dhakaYMD(0)); }
        if (p === '30d') { setFrom(dhakaYMD(-29)); setTo(dhakaYMD(0)); }
    };

    // callRecords.username = কল করা অ্যাডমিনের email (call-history.js এ user.email)
    const callerOf = (c) => String(c.username || '').trim().toLowerCase() || 'Unknown';

    const { loading, calls, failed, total } = state;

    // ধাপ ১: result + search ফিল্টার (সামারি/টেবিল এই লিস্ট থেকে হয়)
    const base = useMemo(() => {
        const q = search.trim().toLowerCase();
        return calls.filter((c) => {
            if (resultF === 'answered' && !c.answered) return false;
            if (resultF === 'unanswered' && c.answered) return false;
            if (q) {
                const hay = `${c.number || ''} ${c.username || ''} ${c.order?.customerInfo?.name || ''} ${c.order?.customerInfo?.phone || ''} ${c.order?.orderId || ''}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [calls, resultF, search]);

    // ধাপ ২: type + caller ফিল্টার (নিচের লিস্ট এটা দেখায়)
    const rows = useMemo(() => base.filter((c) => (
        (typeF === 'all' || c.bucket === typeF) &&
        (callerF === 'all' || callerOf(c) === callerF)
    )), [base, typeF, callerF]); // eslint-disable-line react-hooks/exhaustive-deps

    const stats = useMemo(() => {
        const people = (list) => new Set(list.map((c) => toLocalPhone(c.number) || c.id)).size;
        const summary = {
            calls: rows.length,
            customers: people(rows),
            answered: rows.filter((c) => c.answered).length,
            talk: rows.reduce((s, c) => s + (Number(c.duration) || 0), 0),
        };

        const byType = {};
        TYPES.forEach((t) => {
            const list = base.filter((c) => c.bucket === t.key);
            byType[t.key] = { calls: list.length, customers: people(list), answered: list.filter((c) => c.answered).length };
        });

        const callerMap = new Map();
        base.forEach((c) => {
            const k = callerOf(c);
            if (!callerMap.has(k)) callerMap.set(k, []);
            callerMap.get(k).push(c);
        });
        const byCaller = Array.from(callerMap.entries()).map(([name, list]) => {
            const per = {};
            TYPES.forEach((t) => {
                const l = list.filter((c) => c.bucket === t.key);
                per[t.key] = { calls: l.length, customers: people(l) };
            });
            return {
                name,
                calls: list.length,
                customers: people(list),
                answered: list.filter((c) => c.answered).length,
                talk: list.reduce((s, c) => s + (Number(c.duration) || 0), 0),
                per,
            };
        }).sort((a, b) => b.calls - a.calls);

        return { summary, byType, byCaller };
    }, [rows, base]); // eslint-disable-line react-hooks/exhaustive-deps

    const callerOptions = stats.byCaller.map((r) => r.name);
    const hasFilter = typeF !== 'all' || callerF !== 'all' || resultF !== 'all' || search.trim();
    const clearFilters = () => { setTypeF('all'); setCallerF('all'); setResultF('all'); setSearch(''); };

    const PRESETS = [
        { k: 'today', l: 'আজ' }, { k: 'yesterday', l: 'গতকাল' },
        { k: '7d', l: 'শেষ ৭ দিন' }, { k: '30d', l: 'শেষ ৩০ দিন' }, { k: 'custom', l: 'Custom' },
    ];

    return (
        <div>
            {/* Date filter */}
            <div className="bg-white rounded-xl p-5 mb-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                    <Filter size={18} className="text-orange-600" />
                    <h3 className="text-base font-semibold">Filters</h3>
                    <button
                        onClick={() => setReload((n) => n + 1)}
                        className="ml-auto flex items-center gap-1.5 text-sm text-orange-700 bg-orange-100 hover:bg-orange-200 px-3 py-1.5 rounded-lg transition"
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                    {PRESETS.map((p) => (
                        <button
                            key={p.k}
                            onClick={() => (p.k === 'custom' ? setPreset('custom') : applyPreset(p.k))}
                            className={`px-4 py-1.5 text-sm font-semibold rounded-lg border transition ${preset === p.k
                                ? 'bg-orange-400 text-white border-orange-400'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-orange-50'}`}
                        >
                            {p.l}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">From</label>
                        <input
                            type="date" value={from}
                            onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">To</label>
                        <input
                            type="date" value={to}
                            onChange={(e) => { setPreset('custom'); setTo(e.target.value); }}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">কল করেছে (অ্যাডমিন Email)</label>
                        <select
                            value={callerF} onChange={(e) => setCallerF(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        >
                            <option value="all">সবাই</option>
                            {callerOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">কলের ফলাফল</label>
                        <select
                            value={resultF} onChange={(e) => setResultF(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        >
                            <option value="all">সব কল</option>
                            <option value="answered">ধরেছে (Answered)</option>
                            <option value="unanswered">ধরেনি (No Answer / Busy / Failed)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">নাম / ফোন / Order ID / Email</label>
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                placeholder="নাম / ফোন / email"
                                className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                    </div>
                </div>

                {hasFilter && (
                    <button onClick={clearFilters} className="mt-3 text-sm text-orange-700 hover:underline">
                        সব ফিল্টার মুছুন
                    </button>
                )}
            </div>

            {!loading && failed > 0 && (
                <div className="mb-5 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg">
                    {failed === total
                        ? 'callRecords পড়া যায়নি — Firestore rules এ callRecords পড়ার অনুমতি লাগবে।'
                        : 'callRecords এর একটা সময়-ফিল্ড দিয়ে খোঁজা যায়নি (rules/index), তাই কিছু কল বাদ থাকতে পারে।'}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center items-center h-56">
                    <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-orange-500"></div>
                </div>
            ) : (
                <>
                    {/* Summary */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                            <div className="text-2xl font-bold text-gray-900">{stats.summary.calls}</div>
                            <div className="text-xs text-gray-500">মোট কল</div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <div className="text-2xl font-bold text-gray-900">{stats.summary.customers}</div>
                            <div className="text-xs text-gray-500">কতজন কাস্টমারকে কল (unique)</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                            <div className="text-2xl font-bold text-gray-900">
                                {stats.summary.answered}
                                <span className="text-sm font-normal text-gray-500"> / {stats.summary.calls - stats.summary.answered} ধরেনি</span>
                            </div>
                            <div className="text-xs text-gray-500">Answered</div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <div className="text-2xl font-bold text-gray-900">{fmtDuration(stats.summary.talk)}</div>
                            <div className="text-xs text-gray-500">মোট Talk time</div>
                        </div>
                    </div>

                    {/* By order type */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                        {TYPES.map((t) => {
                            const s = stats.byType[t.key];
                            const active = typeF === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setTypeF(active ? 'all' : t.key)}
                                    className={`text-left rounded-xl p-4 border transition ${t.box} ${active ? 'ring-2 ring-orange-400' : 'hover:shadow'}`}
                                >
                                    <div className="text-xs font-semibold text-gray-600 mb-1">{t.label} অর্ডারের কাস্টমার</div>
                                    <div className={`text-2xl font-bold ${t.num}`}>{s.customers} <span className="text-sm font-normal text-gray-500">জন</span></div>
                                    <div className="text-xs text-gray-500 mt-1">{s.calls} কল · {s.answered} ধরেছে</div>
                                </button>
                            );
                        })}
                    </div>

                    {/* By caller */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
                        <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-800">
                            কে কতজনকে কল করেছে
                            <span className="text-xs font-normal text-gray-500 ml-2">(নামে চাপলে ফিল্টার হবে)</span>
                        </div>
                        {stats.byCaller.length === 0 ? (
                            <p className="text-center text-gray-500 py-8 text-sm">এই সময়ে কোনো কল নেই</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold">অ্যাডমিন Email</th>
                                            <th className="px-3 py-3 text-right font-semibold">কল</th>
                                            <th className="px-3 py-3 text-right font-semibold">কাস্টমার</th>
                                            <th className="px-3 py-3 text-right font-semibold">ধরেছে</th>
                                            <th className="px-3 py-3 text-right font-semibold">Talk</th>
                                            {TYPES.map((t) => (
                                                <th key={t.key} className="px-3 py-3 text-right font-semibold">{t.key === 'Other' ? 'অন্যান্য' : t.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {stats.byCaller.map((r) => (
                                            <tr key={r.name} className={`hover:bg-orange-50 ${callerF === r.name ? 'bg-orange-50' : ''}`}>
                                                <td className="px-4 py-3">
                                                    <button
                                                        onClick={() => setCallerF(callerF === r.name ? 'all' : r.name)}
                                                        className="font-semibold text-orange-700 hover:underline text-left break-all"
                                                    >
                                                        {r.name}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-3 text-right">{r.calls}</td>
                                                <td className="px-3 py-3 text-right font-semibold">{r.customers}</td>
                                                <td className="px-3 py-3 text-right">{r.answered}</td>
                                                <td className="px-3 py-3 text-right">{fmtDuration(r.talk)}</td>
                                                {TYPES.map((t) => (
                                                    <td key={t.key} className="px-3 py-3 text-right">
                                                        <span className="font-semibold">{r.per[t.key].customers}</span>
                                                        <span className="text-xs text-gray-400"> ({r.per[t.key].calls})</span>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
                                    Delivered/Return/Cancel/অন্যান্য কলামে: কাস্টমার সংখ্যা (বন্ধনীতে কল সংখ্যা)
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Call list */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-800">কলের তালিকা</span>
                            <span className="text-xs text-gray-500">({rows.length} টি)</span>
                            {typeF !== 'all' && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_BY_KEY[typeF].badge}`}>{TYPE_BY_KEY[typeF].label}</span>
                            )}
                            {callerF !== 'all' && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">{callerF}</span>
                            )}
                        </div>
                        {rows.length === 0 ? (
                            <p className="text-center text-gray-500 py-10 text-sm">কোনো কল পাওয়া যায়নি</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-semibold">সময়</th>
                                            <th className="px-4 py-3 text-left font-semibold">কাস্টমার</th>
                                            <th className="px-4 py-3 text-left font-semibold">অর্ডার</th>
                                            <th className="px-4 py-3 text-left font-semibold">কল</th>
                                            <th className="px-4 py-3 text-left font-semibold">কে করেছে</th>
                                            <th className="px-4 py-3 text-left font-semibold">Recording</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {rows.slice(0, visible).map((c) => {
                                            const Icon = c.answered ? PhoneIncoming : (c.status === 'No Answer' || c.status === 'Busy') ? PhoneMissed : Phone;
                                            const t = TYPE_BY_KEY[c.bucket];
                                            return (
                                                <tr key={c.id} className="hover:bg-orange-50">
                                                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{fmtCallTime(c)}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="font-semibold text-gray-900">{c.order?.customerInfo?.name || '—'}</div>
                                                        <div className="text-xs text-gray-500">{c.number || c.order?.customerInfo?.phone || '—'}</div>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`text-xs px-2 py-1 rounded ${t.badge}`}>
                                                            {c.order?.status || orphanLabel(c)}
                                                        </span>
                                                        {c.order?.orderId && (
                                                            <div className="text-xs font-mono text-orange-600 mt-1">{String(c.order.orderId).substring(0, 8)}...</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-2">
                                                            <Icon size={15} className="text-orange-500" />
                                                            <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLE[c.status] || 'bg-gray-100 text-gray-600'}`}>
                                                                {c.status || 'Unknown'}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                            <Clock size={11} /> {fmtDuration(c.duration)}
                                                            {c.answered && c.endedBy ? ` · ${c.endedBy === 'admin' ? 'Admin' : 'Customer'} কেটেছে` : ''}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-700 break-all">{callerOf(c)}</td>
                                                    <td className="px-4 py-3">
                                                        {c.recordingUrl
                                                            ? <audio controls preload="none" src={c.recordingUrl} className="h-8 w-48" />
                                                            : <span className="text-gray-300">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {rows.length > visible && (
                                    <div className="p-4 text-center border-t border-gray-100">
                                        <button
                                            onClick={() => setVisible((v) => v + 50)}
                                            className="px-5 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg text-sm font-semibold transition"
                                        >
                                            আরও দেখান ({rows.length - visible} টি বাকি)
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/* ───────────────────────── Tab 2: Next Call Due ───────────────────────── */

const DUE_BUCKETS = [
    { key: 'overdue', label: 'দেরি হয়ে গেছে', test: (d) => d < 0, tone: 'red' },
    { key: 'today', label: 'আজ', test: (d) => d === 0, tone: 'orange' },
    { key: 'tomorrow', label: 'কাল', test: (d) => d === 1, tone: 'blue' },
    { key: 'week', label: 'এই সপ্তাহে (২-৭ দিন)', test: (d) => d >= 2 && d <= 7, tone: 'indigo' },
    { key: 'later', label: '৭ দিনের পরে', test: (d) => d > 7, tone: 'gray' },
];
const TONE = {
    red: 'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
};

function DueTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [typeF, setTypeF] = useState('all');
    const [bucketF, setBucketF] = useState('all');
    const [search, setSearch] = useState('');
    const [reschedule, setReschedule] = useState(null);
    const [newDate, setNewDate] = useState('');
    const [busyId, setBusyId] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setError('');
        getDocs(query(collection(db, 'orders'), where('nextCall.status', '==', 'pending')))
            .then((snap) => { if (alive) setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); })
            .catch((e) => { console.error(e); if (alive) setError('Next Call লিস্ট পড়া যায়নি।'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [reload]);

    const rows = useMemo(() => orders
        .filter((o) => o.nextCall?.date)
        .map((o) => ({ o, days: daysFromToday(o.nextCall.date) }))
        .filter((r) => r.days !== null)
        .sort((a, b) => a.days - b.days), [orders]);

    const q = search.trim().toLowerCase();
    const typed = rows.filter((r) => (typeF === 'all' || bucketOf(r.o.status) === typeF) && (
        !q || `${r.o.customerInfo?.name || ''} ${r.o.customerInfo?.phone || ''} ${r.o.orderId || ''}`.toLowerCase().includes(q)
    ));
    const shown = typed.filter((r) => bucketF === 'all' || DUE_BUCKETS.find((b) => b.key === bucketF).test(r.days));

    const markDone = async (order) => {
        setBusyId(order.id);
        try {
            await updateDoc(doc(db, 'orders', order.id), {
                'nextCall.status': 'done',
                'nextCall.doneAt': Timestamp.now(),
                'nextCall.doneBy': auth.currentUser?.email || '',
            });
            setOrders((prev) => prev.filter((x) => x.id !== order.id));
        } catch (e) {
            console.error(e);
            alert('Done করা যায়নি');
        } finally {
            setBusyId('');
        }
    };

    const saveReschedule = async () => {
        if (!reschedule || !newDate) return;
        setBusyId(reschedule.id);
        try {
            const nextCall = {
                date: newDate,
                status: 'pending',
                setBy: auth.currentUser?.email || '',
                setAt: Timestamp.now(),
            };
            await updateDoc(doc(db, 'orders', reschedule.id), { nextCall });
            setOrders((prev) => prev.map((x) => (x.id === reschedule.id ? { ...x, nextCall } : x)));
            setReschedule(null);
            setNewDate('');
        } catch (e) {
            console.error(e);
            alert('তারিখ বদলানো যায়নি');
        } finally {
            setBusyId('');
        }
    };

    return (
        <div>
            <div className="bg-white rounded-xl p-5 mb-5 border border-gray-200">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                        <Filter size={18} className="text-orange-600" />
                        <h3 className="text-base font-semibold">Filters</h3>
                    </div>
                    <select
                        value={typeF} onChange={(e) => setTypeF(e.target.value)}
                        className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                        <option value="all">সব অর্ডার টাইপ</option>
                        {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="নাম / ফোন / Order ID"
                            className="pl-8 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                    </div>
                    <button
                        onClick={() => setReload((n) => n + 1)}
                        className="ml-auto flex items-center gap-1.5 text-sm text-orange-700 bg-orange-100 hover:bg-orange-200 px-3 py-2 rounded-lg transition"
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>
            </div>

            {/* Bucket chips */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                {DUE_BUCKETS.map((b) => {
                    const n = typed.filter((r) => b.test(r.days)).length;
                    const active = bucketF === b.key;
                    return (
                        <button
                            key={b.key}
                            onClick={() => setBucketF(active ? 'all' : b.key)}
                            className={`text-left rounded-xl p-4 border transition ${TONE[b.tone]} ${active ? 'ring-2 ring-orange-400' : 'hover:shadow'}`}
                        >
                            <div className="text-2xl font-bold">{n}</div>
                            <div className="text-xs">{b.label}</div>
                        </button>
                    );
                })}
            </div>

            {error && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg">{error}</div>}

            {loading ? (
                <div className="flex justify-center items-center h-48">
                    <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-orange-500"></div>
                </div>
            ) : shown.length === 0 ? (
                <div className="text-center py-14 text-gray-500">
                    {rows.length === 0
                        ? 'কোনো Next Call সেট করা নেই। Cancel / Delivered / Return পেজে Call Note দেওয়ার সময় "পরবর্তী কল কবে" বেছে নিন।'
                        : 'এই ফিল্টারে কিছু নেই'}
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold">কবে কল</th>
                                    <th className="px-4 py-3 text-left font-semibold">কাস্টমার</th>
                                    <th className="px-4 py-3 text-left font-semibold">অর্ডার</th>
                                    <th className="px-4 py-3 text-left font-semibold">শেষ Note / Lead</th>
                                    <th className="px-4 py-3 text-left font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {shown.map(({ o, days }) => {
                                    const t = TYPE_BY_KEY[bucketOf(o.status)];
                                    const tone = days < 0 ? 'text-red-600' : days === 0 ? 'text-orange-600' : 'text-blue-600';
                                    const clean = String(o.customerInfo?.phone || '').replace(/[^0-9]/g, '');
                                    return (
                                        <tr key={o.id} className="hover:bg-orange-50">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="font-semibold text-gray-900">{fmtYMD(o.nextCall.date)}</div>
                                                <div className={`text-xs font-semibold ${tone}`}>
                                                    {days < 0 ? `${-days} দিন দেরি` : days === 0 ? 'আজ' : days === 1 ? 'কাল' : `${days} দিন পর`}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-semibold text-gray-900">{o.customerInfo?.name || 'N/A'}</div>
                                                <div className="text-xs text-gray-500">{o.customerInfo?.phone || 'N/A'}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-xs px-2 py-1 rounded ${t.badge}`}>{o.status || '—'}</span>
                                                {o.orderId && <div className="text-xs font-mono text-orange-600 mt-1">{String(o.orderId).substring(0, 8)}...</div>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-600">
                                                {o.leadStatus ? o.leadStatus.replace('_', ' ') : '—'}
                                                {o.nextCall.setBy && <div className="text-gray-400 break-all">সেট করেছে: {o.nextCall.setBy}</div>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-2">
                                                    <button
                                                        onClick={() => { if (clean) window.location.href = `sip:${o.customerInfo?.phone}`; }}
                                                        className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition" title="Call via Port SIP"
                                                    ><Phone size={16} /></button>
                                                    <button
                                                        onClick={() => { if (clean) window.open(`https://wa.me/${clean}`, '_blank'); }}
                                                        className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition" title="WhatsApp"
                                                    ><MessageCircle size={16} /></button>
                                                    <button
                                                        onClick={() => { setReschedule(o); setNewDate(o.nextCall.date); }}
                                                        className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg transition" title="তারিখ বদলান"
                                                    ><CalendarClock size={16} /></button>
                                                    <button
                                                        disabled={busyId === o.id}
                                                        onClick={() => markDone(o)}
                                                        className="p-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition disabled:opacity-50" title="Call হয়ে গেছে (Done)"
                                                    ><Check size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {reschedule && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Next Call তারিখ বদলান</h2>
                            <button onClick={() => { setReschedule(null); setNewDate(''); }} className="text-gray-500 hover:text-gray-900"><X size={22} /></button>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            {reschedule.customerInfo?.name || 'N/A'} — {reschedule.customerInfo?.phone || 'N/A'}
                        </p>
                        <NextCallPicker value={newDate} onChange={setNewDate} />
                        <button
                            onClick={saveReschedule}
                            disabled={!newDate || busyId === reschedule.id}
                            className="w-full bg-orange-400 text-white hover:bg-orange-500 font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ───────────────────────── Page ───────────────────────── */

function CallReportPage() {
    const [tab, setTab] = useState('report');
    const [userEmail, setUserEmail] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);
    const navigate = useNavigate();

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (user) setUserEmail(user.email);
            else navigate('/login');
        });
        return () => unsub();
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const TABS = [
        { k: 'report', l: 'Call Report', Icon: PhoneIncoming },
        { k: 'due', l: 'Next Call Due', Icon: CalendarClock },
    ];

    return (
        <div className="min-h-screen bg-gray-50 flex text-gray-900">
            <DashboardNav sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 min-w-0 p-6 overflow-auto">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="p-2 hover:bg-orange-50 rounded-lg transition"
                            >
                                <Menu size={24} />
                            </button>
                            <div>
                                <h1 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                                    Call Report
                                </h1>
                                <p className="text-sm text-gray-500">Logged in as: {userEmail}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg transition duration-200"
                        >
                            <LogOut size={20} />
                            Logout
                        </button>
                    </div>

                    <div className="flex gap-2 mb-5 border-b border-gray-200">
                        {TABS.map(({ k, l, Icon }) => (
                            <button
                                key={k}
                                onClick={() => setTab(k)}
                                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition ${tab === k
                                    ? 'border-orange-400 text-orange-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                            >
                                <Icon size={16} /> {l}
                            </button>
                        ))}
                    </div>

                    {tab === 'report' ? <ReportTab /> : <DueTab />}
                </div>
            </div>
        </div>
    );
}

export default CallReportPage;
